import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, TextField, MenuItem, Stack, InputAdornment,
  IconButton, Tooltip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Alert, CircularProgress, Skeleton, Checkbox, Tabs, Tab, Divider,
} from '@mui/material';
import { Edit, Search, PersonAdd, GroupAdd, BarChart, Close, People, Straighten, AssignmentTurnedIn, WorkspacePremium, AutoAwesome, PhoneInTalk, PauseCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usersAPI, aiPlansAPI, aiMealPlansAPI, pauseAPI } from '../services/api';
import { maskPhone } from '../utils/masking';
import KycSection from '../components/KycSection';
import { useAppData } from '../context/AppDataContext';
import AssignPlanDialog from '../components/AssignPlanDialog';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';

// Google 'G' icon as inline SVG
const GoogleIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function Users() {
  const navigate = useNavigate();
  const { gyms } = useAppData();
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const isSuperadmin = currentUser?.role === 'superadmin';
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [gymFilter, setGymFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignPlanType, setAssignPlanType] = useState('workout');
  const [page, setPage]   = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [aiPlansUser, setAiPlansUser] = useState(null);
  const [freezeUser, setFreezeUser] = useState(null);
  const batchOptions = Array.from(new Set(users.map((user) => user.batchName).filter(Boolean))).sort();

  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, gymFilter, page, limit, searchQuery]);

  // Reset to page 1 when filter or search changes
  useEffect(() => { setPage(1); }, [filter, gymFilter, searchQuery]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filter === 'all') {
        params.role = 'user';
      } else if (filter === 'premium') {
        params.role = 'user';
        params.isPremium = true;
      }
      if (gymFilter !== 'all') {
        params.gymId = gymFilter;
      }
      if (searchQuery) params.search = searchQuery;
      const response = await usersAPI.getAll(params);
      setUsers(response.data.data);
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (userData) => {
    await usersAPI.create(userData);
    setSuccess('User created successfully');
    setAddOpen(false);
    loadUsers();
  };

  const openEdit = (user) => {
    setEditingUser({ ...user, gymId: user.gymId?._id || user.gymId || '' });
    setEditOpen(true);
  };

  const handleEditSave = async (updated) => {
    await usersAPI.update(updated._id, updated);
    setSuccess('User updated successfully');
    setEditOpen(false);
    setEditingUser(null);
    loadUsers();
  };

  // Status chip: inline sx tokens
  const getStatusSx = (status) => {
    switch (status) {
      case 'active':    return { bgcolor: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0' };
      case 'cancelled': return { bgcolor: '#FFF1F2', color: '#E11D48', border: '1px solid #FECDD3' };
      case 'expired':   return { bgcolor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' };
      case 'inactive':
      default:          return { bgcolor: 'background.paper', color: 'text.secondary', border: '1px solid', borderColor: 'divider' };
    }
  };

  // Role chip: inline sx tokens
  const getRoleSx = (role) => {
    switch (role) {
      case 'admin':      return { bgcolor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' };
      case 'trainer':    return { bgcolor: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A' };
      case 'gym_admin':  return { bgcolor: '#ECFEFF', color: '#0891B2', border: '1px solid #A5F3FC' };
      case 'user':
      default:           return { bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' };
    }
  };

  // With server-side pagination, search goes to the API — no client-side filter needed
  const filteredUsers = users;

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle="Manage gym members and member subscriptions"
        gradient="linear-gradient(90deg, #7C3AED, #A78BFA)"
        meta={!loading && (
          <Box sx={{ px: 1, py: 0.25, borderRadius: 1.5, bgcolor: 'action.hover' }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>{total}</Typography>
          </Box>
        )}
        actions={<>
          {selectedUsers.length > 0 && <>
            <Button variant="outlined" size="small" startIcon={<AssignmentTurnedIn />}
              onClick={() => { setAssignPlanType('workout'); setAssignOpen(true); }}>
              Workout ({selectedUsers.length})
            </Button>
            <Button variant="outlined" size="small" startIcon={<AssignmentTurnedIn />}
              onClick={() => { setAssignPlanType('meal'); setAssignOpen(true); }}
              sx={{ borderColor: '#D97706', color: '#D97706' }}>
              Meal ({selectedUsers.length})
            </Button>
          </>}
          <Button variant="outlined" size="small" startIcon={<GroupAdd />} onClick={() => navigate('/dashboard/batches')}>Batches</Button>
          <Button variant="contained" size="small" startIcon={<PersonAdd />} onClick={() => setAddOpen(true)}>Add User</Button>
        </>}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          placeholder="Search by name or email…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        {isSuperadmin && (
          <TextField
            select value={gymFilter}
            onChange={(e) => setGymFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Gyms</MenuItem>
            {gyms.map(gym => (
              <MenuItem key={gym._id} value={gym._id}>{gym.name}</MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">Members</MenuItem>
          <MenuItem value="premium">Premium</MenuItem>
        </TextField>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}
      >
        <Table sx={{ minWidth: 1080, '& .MuiTableCell-root': { py: 1.5 } }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
            <TableRow sx={{ '& .MuiTableCell-root': { bgcolor: 'background.paper', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' } }}>
              <TableCell padding="checkbox" sx={{ pl: 2 }}>
                <Checkbox
                  size="small"
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                  checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                  onChange={(e) => setSelectedUsers(e.target.checked ? filteredUsers.map(u => u) : [])}
                />
              </TableCell>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Batch</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Preferred Time</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Gym</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Auth</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Premium</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Workouts</TableCell>
              <TableCell align="right" sx={{ pr: 3, minWidth: 190, whiteSpace: 'nowrap' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? [...Array(6)].map((_, i) => (
                  <TableRow key={i} sx={{ minHeight: 56 }}>
                    <TableCell padding="checkbox" sx={{ pl: 2 }}><Skeleton variant="rectangular" width={16} height={16} sx={{ borderRadius: 1 }} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Skeleton variant="circular" width={34} height={34} />
                        <Box>
                          <Skeleton width={110} height={14} sx={{ mb: 0.5 }} />
                          <Skeleton width={150} height={12} />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Skeleton width={64} height={24} sx={{ borderRadius: 3 }} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Skeleton width={80} height={14} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}><Skeleton width={70} height={14} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton width={80} height={14} /></TableCell>
                    <TableCell><Skeleton width={64} height={24} sx={{ borderRadius: 3 }} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}><Skeleton width={60} height={24} sx={{ borderRadius: 3 }} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Skeleton width={70} height={24} sx={{ borderRadius: 3 }} /></TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}><Skeleton width={30} height={14} /></TableCell>
                    <TableCell align="right" sx={{ pr: 3, minWidth: 190 }}><Skeleton width={120} height={28} sx={{ borderRadius: 2, ml: 'auto' }} /></TableCell>
                  </TableRow>
                ))
              : filteredUsers.map((user) => (
                  <TableRow key={user._id}
                    sx={{ '&:hover': { bgcolor: 'background.paper', '& .MuiTableCell-root': { borderBottom: '1px solid', borderColor: 'divider' } }, transition: 'background-color 0.15s' }}
                  >
                    <TableCell padding="checkbox" sx={{ pl: 2 }}>
                      <Checkbox
                        size="small"
                        checked={!!selectedUsers.find(u => u._id === user._id)}
                        onChange={(e) => {
                          setSelectedUsers(prev =>
                            e.target.checked ? [...prev, user] : prev.filter(u => u._id !== user._id)
                          );
                        }}
                      />
                    </TableCell>
                    {/* User */}
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 34, height: 34, fontSize: '0.8125rem', fontWeight: 700, bgcolor: 'text.primary', color: '#fff' }}>
                          {user.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>{user.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    {/* Role */}
                    <TableCell>
                      <Chip label={user.role} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', ...getRoleSx(user.role) }} />
                    </TableCell>
                    {/* Batch */}
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary">{user.batchName || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary">{user.preferredTime || '—'}</Typography>
                    </TableCell>
                    {/* Gym */}
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary">{user.gymId?.name || '—'}</Typography>
                    </TableCell>
                    {/* Status */}
                    <TableCell>
                      <Chip label={user.membershipStatus || 'inactive'}
                        size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', ...getStatusSx(user.membershipStatus) }} />
                    </TableCell>
                    {/* Auth */}
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {user.googleId ? (
                        <Chip icon={<GoogleIcon />} label="Google" size="small"
                          sx={{ fontWeight: 600, bgcolor: '#F0F4FF', color: '#4285F4', border: '1px solid #C5D3F5', '& .MuiChip-icon': { ml: 0.5 } }} />
                      ) : (
                        <Chip label="Email" size="small" sx={{ fontWeight: 600, bgcolor: 'background.paper', color: 'text.secondary' }} />
                      )}
                    </TableCell>
                    {/* Premium */}
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {user.isPremium ? (
                        <Tooltip title={user.premiumExpiry ? `Expires: ${new Date(user.premiumExpiry).toLocaleDateString()}` : 'Lifetime'}>
                          <Chip icon={<WorkspacePremium sx={{ fontSize: '0.85rem !important' }} />} label="Premium" size="small"
                            sx={{ fontWeight: 700, bgcolor: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A', '& .MuiChip-icon': { color: '#D97706', ml: 0.5 } }} />
                        </Tooltip>
                      ) : (
                        <Chip label="Free" size="small" sx={{ fontWeight: 600, bgcolor: 'background.paper', color: 'text.secondary' }} />
                      )}
                    </TableCell>
                    {/* Workouts */}
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">{user.totalWorkouts || 0}</Typography>
                    </TableCell>
                    {/* Actions */}
                    <TableCell align="right" sx={{ pr: 3, minWidth: 190, whiteSpace: 'nowrap' }}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                        <Tooltip title="Analytics">
                          <IconButton size="small" onClick={() => navigate(`/dashboard/users/${user._id}/analytics`)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}>
                            <BarChart sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Measurements">
                          <IconButton size="small" onClick={() => navigate(`/dashboard/users/${user._id}/measurements`)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}>
                            <Straighten sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="AI Plans">
                          <IconButton size="small" onClick={() => setAiPlansUser(user)}
                            sx={{ color: 'text.secondary', '&:hover': { color: '#7C3AED', bgcolor: '#F5F3FF' } }}>
                            <AutoAwesome sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Freeze / Pause Membership">
                          <IconButton size="small" onClick={() => setFreezeUser(user)}
                            sx={{ color: 'text.secondary', '&:hover': { color: '#D97706', bgcolor: '#FEF3C7' } }}>
                            <PauseCircle sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(user)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}>
                            <Edit sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationBar page={page} pages={pages} total={total} limit={limit} onPage={p => { setPage(p); setSelectedUsers([]); }} onLimit={v => { setLimit(v); setPage(1); setSelectedUsers([]); }} />

      {!loading && filteredUsers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10, px: 2 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: 4, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
            <People sx={{ fontSize: 40, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
            {searchQuery ? `No results for "${searchQuery}"` : 'No users yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 320, mx: 'auto' }}>
            {searchQuery
              ? 'Try a different name or email address'
              : 'Add your first user to get started managing your gym members'}
          </Typography>
          {!searchQuery && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
              <Button variant="outlined" startIcon={<GroupAdd />} onClick={() => navigate('/dashboard/batches')}
                sx={{ minWidth: 160 }}>
                Batches
              </Button>
              <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setAddOpen(true)}
                sx={{ minWidth: 160 }}>
                Add First User
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {editOpen && editingUser && (
        <EditUserDialog
          user={editingUser}
          gyms={gyms}
          onSave={handleEditSave}
          onClose={() => { setEditOpen(false); setEditingUser(null); }}
        />
      )}

      {addOpen && (
        <AddUserDialog
          gyms={gyms}
          batchOptions={batchOptions}
          onSave={handleAddUser}
          onClose={() => setAddOpen(false)}
        />
      )}

      {/* Assign Plan Dialog */}
      <AssignPlanDialog
        open={assignOpen}
        onClose={() => { setAssignOpen(false); setSelectedUsers([]); setSuccess('Plan assigned successfully'); }}
        planType={assignPlanType}
        preselectedUsers={selectedUsers}
      />

      {/* AI Plans Dialog — superadmin only */}
      {aiPlansUser && (
        <AiPlansDialog user={aiPlansUser} onClose={() => setAiPlansUser(null)} />
      )}

      {/* Freeze / Membership Pause Modal */}
      {freezeUser && (
        <FreezeMembershipModal user={freezeUser} onClose={() => setFreezeUser(null)} onRefresh={loadUsers} />
      )}
    </Box>
  );
}

// Helper to get gym name from gyms list by id (handles string or object)
function getGymName(gyms, gymId) {
  if (!gymId) return '';
  const id = typeof gymId === 'object' ? gymId._id || gymId.toString() : gymId;
  const gym = gyms.find(g => g._id?.toString() === id?.toString());
  return gym?.name || id;
}

// Helper to get gym id string
function getGymId(gymId) {
  if (!gymId) return '';
  return typeof gymId === 'object' ? gymId._id || '' : gymId;
}

// ── Edit User Dialog ───────────────────────────────────────────────────────────
function EditUserDialog({ user: initialUser, gyms, onSave, onClose }) {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const isSuperadmin = currentUser?.role === 'superadmin';

  const [user, setUser] = useState({ ...initialUser, gymId: getGymId(initialUser.gymId) });
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (field, value) => setUser(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!user.name?.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try {
      await onSave(user);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit User</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {user.googleId && (
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.8125rem' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <GoogleIcon /> This user signed up with Google. Password login is not available.
            </Box>
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={(e, value) => setTab(value)} textColor="primary" indicatorColor="primary" variant="fullWidth">
            <Tab label="Details" />
            <Tab label="KYC Documents" />
            <Tab label="Medical & Contracts" />
          </Tabs>
        </Box>

        {tab === 0 && (
          <>
            {/* Account section */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Account
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Name" value={user.name || ''} onChange={e => update('name', e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Email" value={user.email || ''} onChange={e => update('email', e.target.value)} fullWidth type="email" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Phone Number" value={user.phone || ''} onChange={e => update('phone', e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="eSSL Biometric Device ID" 
                value={user.esslEmpCode || ''} 
                onChange={e => update('esslEmpCode', e.target.value)} 
                fullWidth 
                helperText="Enter the ID assigned to this member on your biometric scanner" 
              />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Role" value="Member" fullWidth disabled />
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Membership Status" value={user.membershipStatus || 'inactive'} onChange={e => update('membershipStatus', e.target.value)} fullWidth>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              {isSuperadmin ? (
                <TextField select label="Gym" value={user.gymId || ''} onChange={e => update('gymId', e.target.value)} fullWidth>
                  <MenuItem value="">No Gym</MenuItem>
                  {gyms.map(g => <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>)}
                </TextField>
              ) : (
                <TextField label="Gym" value={getGymName(gyms, user.gymId)} fullWidth disabled />
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Gender" value={user.gender || ''} onChange={e => update('gender', e.target.value)} fullWidth>
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Batch" value={user.batchName || ''} onChange={e => update('batchName', e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Preferred Time"
                type="time"
                value={user.preferredTime || ''}
                onChange={e => update('preferredTime', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Date of Birth"
                type="date"
                value={user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : ''}
                onChange={e => update('dateOfBirth', e.target.value || null)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {initialUser.gymId && !initialUser.gymId?.isDefault && (
              <Grid item xs={12}>
                <TextField
                  label="Emergency Contact Number"
                  value={user.emergencyContactNumber ? maskPhone(user.emergencyContactNumber) : 'Not added'}
                  fullWidth
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneInTalk sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Added by the member in the Fitistic app"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Physical stats section */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Physical Stats
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Age" type="number" value={user.age || ''} onChange={e => update('age', e.target.value ? Number(e.target.value) : '')} fullWidth inputProps={{ min: 1, max: 120 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Height (cm)" type="number" value={user.height || ''} onChange={e => update('height', e.target.value ? Number(e.target.value) : '')} fullWidth inputProps={{ min: 50, max: 300 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Weight (kg)" type="number" value={user.weight || ''} onChange={e => update('weight', e.target.value ? Number(e.target.value) : '')} fullWidth inputProps={{ min: 10, max: 500 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Fitness Goal" value={user.fitnessGoal || ''} onChange={e => update('fitnessGoal', e.target.value)} fullWidth>
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="lose_weight">Lose Weight</MenuItem>
                <MenuItem value="build_muscle">Build Muscle</MenuItem>
                <MenuItem value="stay_fit">Stay Fit</MenuItem>
                <MenuItem value="increase_endurance">Increase Endurance</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      </>
        )}
        
        {tab === 1 && (
          <KycSection userId={initialUser._id} gymId={getGymId(initialUser.gymId)} />
        )}

        {tab === 2 && (
          <>
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                Medical Flags
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField select label="Has Medical Condition" value={user.hasMedicalCondition ? 'yes' : 'no'} onChange={e => update('hasMedicalCondition', e.target.value === 'yes')} fullWidth>
                    <MenuItem value="no">No</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Medical Notes" multiline rows={3} value={user.medicalNotes || ''} onChange={e => update('medicalNotes', e.target.value)} fullWidth placeholder="E.g., Asthma, previous back injury, etc." />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                Contracts & Waivers
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField label="Waiver Accepted At" type="datetime-local" value={user.waiverAcceptedAt ? new Date(user.waiverAcceptedAt).toISOString().slice(0,16) : ''} onChange={e => update('waiverAcceptedAt', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Digital Signature URL" value={user.digitalSignatureUrl || ''} onChange={e => update('digitalSignatureUrl', e.target.value)} fullWidth placeholder="https://example.com/signature.png" />
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Add User Dialog ────────────────────────────────────────────────────────────
function AddUserDialog({ gyms, batchOptions, onSave, onClose }) {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const isSuperadmin = currentUser?.role === 'superadmin';

  const [form, setForm] = useState({
    name: '', email: '', password: '', role: 'user',
    gymId: getGymId(currentUser?.gymId) || '',
    batchName: '',
    preferredTime: '',
    dateOfBirth: '',
    gender: '', age: '', height: '', weight: '',
    fitnessGoal: '', membershipStatus: 'active',
    phone: '', esslEmpCode: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.name?.trim()) { setError('Name is required'); return; }
    if (!form.email?.trim()) { setError('Email is required'); return; }
    if (!form.password?.trim()) { setError('Password is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : undefined,
        height: form.height ? Number(form.height) : undefined,
        weight: form.weight ? Number(form.weight) : undefined,
        dateOfBirth: form.dateOfBirth || undefined,
        gymId: form.gymId || undefined,
      };
      await onSave(payload);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create user');
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add User</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Account section */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Account
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Name *" value={form.name} onChange={e => update('name', e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Email *" value={form.email} onChange={e => update('email', e.target.value)} fullWidth type="email" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Phone Number" value={form.phone} onChange={e => update('phone', e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="eSSL Biometric Device ID" 
                value={form.esslEmpCode} 
                onChange={e => update('esslEmpCode', e.target.value)} 
                fullWidth 
                helperText="Enter the ID assigned to this member on your biometric scanner" 
              />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Password *" value={form.password} onChange={e => update('password', e.target.value)} fullWidth type="password" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Role" value="Member" fullWidth disabled />
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Membership Status" value={form.membershipStatus} onChange={e => update('membershipStatus', e.target.value)} fullWidth>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              {isSuperadmin ? (
                <TextField select label="Gym" value={form.gymId} onChange={e => update('gymId', e.target.value)} fullWidth>
                  <MenuItem value="">No Gym</MenuItem>
                  {gyms.map(g => <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>)}
                </TextField>
              ) : (
                <TextField label="Gym" value={getGymName(gyms, form.gymId)} fullWidth disabled helperText="Users are added to your gym" />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Batch (Optional)"
                value={form.batchName}
                onChange={(e) => update('batchName', e.target.value)}
                fullWidth
                helperText="Assign this member to an existing batch"
              >
                <MenuItem value="">None</MenuItem>
                {batchOptions.map((batchName) => (
                  <MenuItem key={batchName} value={batchName}>{batchName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Preferred Time"
                type="time"
                value={form.preferredTime}
                onChange={(e) => update('preferredTime', e.target.value)}
                fullWidth
                helperText="Optional preferred workout or class time"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Date of Birth"
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => update('dateOfBirth', e.target.value)}
                fullWidth
                helperText="Used to calculate age automatically"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Physical stats section */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Physical Stats
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField select label="Gender" value={form.gender} onChange={e => update('gender', e.target.value)} fullWidth>
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Fitness Goal" value={form.fitnessGoal} onChange={e => update('fitnessGoal', e.target.value)} fullWidth>
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="lose_weight">Lose Weight</MenuItem>
                <MenuItem value="build_muscle">Build Muscle</MenuItem>
                <MenuItem value="stay_fit">Stay Fit</MenuItem>
                <MenuItem value="increase_endurance">Increase Endurance</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField label="Age" type="number" value={form.age} onChange={e => update('age', e.target.value)} fullWidth inputProps={{ min: 1, max: 120 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Height (cm)" type="number" value={form.height} onChange={e => update('height', e.target.value)} fullWidth inputProps={{ min: 50, max: 300 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Weight (kg)" type="number" value={form.weight} onChange={e => update('weight', e.target.value)} fullWidth inputProps={{ min: 10, max: 500 }} />
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Creating...' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// eslint-disable-next-line no-unused-vars
function BatchUserDialog({ gyms, onSave, onClose }) {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const isSuperadmin = currentUser?.role === 'superadmin';
  const [batchName, setBatchName] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [rawUsers, setRawUsers] = useState('');
  const [gymId, setGymId] = useState(getGymId(currentUser?.gymId) || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const parseUsers = () => rawUsers
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name, email, linePreferredTime] = line.split(',').map((part) => part.trim());
      return { name, email, preferredTime: linePreferredTime || preferredTime };
    });

  const handleSave = async () => {
    if (!batchName.trim()) { setError('Batch name is required'); return; }
    const users = parseUsers().filter((user) => user.name && user.email);
    if (users.length === 0) { setError('Enter at least one user as Name,email'); return; }
    setSaving(true);
    try {
      await onSave({ batchName: batchName.trim(), preferredTime: preferredTime || undefined, gymId: gymId || undefined, users });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create batch users');
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create Batch Users</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Batch Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Batch Name" value={batchName} onChange={(e) => setBatchName(e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Preferred Time"
                type="time"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                fullWidth
                helperText="Default time for this batch. You can override per member in the list."
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              {isSuperadmin ? (
                <TextField select label="Gym" value={gymId} onChange={(e) => setGymId(e.target.value)} fullWidth>
                  <MenuItem value="">No Gym</MenuItem>
                  {gyms.map((g) => <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>)}
                </TextField>
              ) : (
                <TextField label="Gym" value={getGymName(gyms, gymId)} fullWidth disabled helperText="Members are added to your gym" />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Users"
                value={rawUsers}
                onChange={(e) => setRawUsers(e.target.value)}
                fullWidth
                multiline
                rows={6}
                helperText="Enter one member per line: Name,email or Name,email,preferred time"
              />
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Creating...' : 'Create Batch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── AI Plans Dialog — superadmin only ─────────────────────────────────────────
function AiPlansDialog({ user, onClose }) {
  const [tab, setTab]             = useState(0); // 0 = workout/yoga, 1 = meal
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [mealPlans, setMealPlans]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [wpRes, mpRes] = await Promise.all([
          aiPlansAPI.getUserPlans(user._id),
          aiMealPlansAPI.getUserMealPlans(user._id),
        ]);
        setWorkoutPlans(wpRes.data.data || []);
        setMealPlans(mpRes.data.data || []);
      } catch (e) {
        setError(e.response?.data?.message || 'Failed to load AI plans');
      } finally {
        setLoading(false);
      }
    })();
  }, [user._id]);

  const typeColor = { workout: '#4CAF82', yoga: '#9C27B0' };
  const typeIcon  = { workout: '🏋️', yoga: '🧘' };
  const dietColor = { regular: '#2196F3', vegetarian: '#4CAF50', vegan: '#8BC34A', 'non-veg': '#F44336', keto: '#FF9800', paleo: '#795548' };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const totalPlans = workoutPlans.length + mealPlans.length;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesome sx={{ color: '#7C4DFF' }} />
            <Typography variant="h6">AI Plans — {user.name}</Typography>
          </Stack>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary">{user.email}</Typography>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40 }}>
          <Tab
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>Workout & Yoga</span>
                {!loading && (
                  <Chip label={workoutPlans.length} size="small"
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#4CAF8222', color: '#4CAF82' }} />
                )}
              </Stack>
            }
            sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
          />
          <Tab
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>Meal Plans</span>
                {!loading && (
                  <Chip label={mealPlans.length} size="small"
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#FF980022', color: '#FF9800' }} />
                )}
              </Stack>
            }
            sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
          />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ minHeight: 320 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : tab === 0 ? (
          /* ── Workout / Yoga plans ── */
          workoutPlans.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography fontSize={40} mb={1}>🏋️</Typography>
              <Typography variant="body2" color="text.secondary">No workout or yoga plans generated yet.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {workoutPlans.map((plan) => {
                const color = typeColor[plan.planType] || '#4CAF82';
                const icon  = typeIcon[plan.planType]  || '📋';
                const activeDays = plan.days?.filter(d => !d.isRestDay).length || 0;
                const totalEx    = plan.days?.reduce((s, d) => s + (d.exercises?.length || 0), 0) || 0;
                return (
                  <Grid item xs={12} sm={6} key={plan._id}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: color + '44', height: '100%' }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={1.5}>
                        <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: color + '22',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                          {icon}
                        </Box>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="subtitle2" fontWeight={700} noWrap>{plan.name}</Typography>
                          {plan.description && (
                            <Typography variant="caption" color="text.secondary" noWrap display="block">{plan.description}</Typography>
                          )}
                        </Box>
                        <Chip label={plan.planType} size="small"
                          sx={{ bgcolor: color + '22', color, fontWeight: 700, fontSize: 10 }} />
                      </Stack>

                      {/* Stats row */}
                      <Stack direction="row" spacing={1.5} mb={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`📅 ${plan.days?.length || 0} days`}
                          sx={{ bgcolor: 'grey.100', fontSize: 10, height: 22 }} />
                        <Chip size="small" label={`💪 ${activeDays} active`}
                          sx={{ bgcolor: 'grey.100', fontSize: 10, height: 22 }} />
                        <Chip size="small" label={`🏃 ${totalEx} exercises`}
                          sx={{ bgcolor: 'grey.100', fontSize: 10, height: 22 }} />
                        {plan.circuitMode && (
                          <Chip size="small" label="⚡ Circuit"
                            sx={{ bgcolor: '#FF980022', color: '#FF9800', fontSize: 10, height: 22, fontWeight: 700 }} />
                        )}
                      </Stack>

                      {plan.generationPrompt && (
                        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, px: 1.5, py: 0.75, mb: 1,
                          border: '1px solid', borderColor: 'grey.200' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            "{plan.generationPrompt}"
                          </Typography>
                        </Box>
                      )}

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.disabled">{formatDate(plan.updatedAt || plan.createdAt)}</Typography>
                        <Chip label="AI Generated" size="small"
                          icon={<AutoAwesome sx={{ fontSize: '12px !important' }} />}
                          sx={{ bgcolor: '#7C4DFF22', color: '#7C4DFF', fontWeight: 600, fontSize: 10 }} />
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )
        ) : (
          /* ── Meal plans ── */
          mealPlans.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography fontSize={40} mb={1}>🥗</Typography>
              <Typography variant="body2" color="text.secondary">No AI meal plans generated yet.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {mealPlans.map((plan) => {
                const dc = dietColor[plan.dietType] || '#FF9800';
                return (
                  <Grid item xs={12} sm={6} key={plan._id}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: '#FF980044', height: '100%' }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={1.5}>
                        <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#FF980022',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                          🥗
                        </Box>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="subtitle2" fontWeight={700} noWrap>{plan.name}</Typography>
                          {plan.description && (
                            <Typography variant="caption" color="text.secondary" noWrap display="block">{plan.description}</Typography>
                          )}
                        </Box>
                        <Chip label={plan.dietType || 'regular'} size="small"
                          sx={{ bgcolor: dc + '22', color: dc, fontWeight: 700, fontSize: 10, textTransform: 'capitalize' }} />
                      </Stack>

                      {/* Macro targets */}
                      <Grid container spacing={0.75} mb={1}>
                        {[
                          { label: 'Calories', value: `${plan.targetCalories || 0} kcal`, color: '#FF9800' },
                          { label: 'Protein',  value: `${Math.round(plan.targetProteinG || 0)}g`,  color: '#4CAF82' },
                          { label: 'Carbs',    value: `${Math.round(plan.targetCarbsG || 0)}g`,    color: '#2196F3' },
                          { label: 'Fat',      value: `${Math.round(plan.targetFatG || 0)}g`,      color: '#9C27B0' },
                        ].map(({ label, value, color: mc }) => (
                          <Grid item xs={6} key={label}>
                            <Box sx={{ bgcolor: mc + '11', borderRadius: 1.5, px: 1, py: 0.5,
                              border: '1px solid', borderColor: mc + '33' }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 9, lineHeight: 1.2 }}>
                                {label}
                              </Typography>
                              <Typography variant="caption" fontWeight={700} sx={{ color: mc, fontSize: 11 }}>
                                {value}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      {/* Meals count */}
                      <Stack direction="row" spacing={1} mb={1}>
                        <Chip size="small" label={`🍽️ ${plan.meals?.length || 0} meals/day`}
                          sx={{ bgcolor: 'grey.100', fontSize: 10, height: 22 }} />
                        <Chip size="small" label={`🥘 ${plan.meals?.reduce((s, m) => s + (m.items?.length || 0), 0) || 0} food items`}
                          sx={{ bgcolor: 'grey.100', fontSize: 10, height: 22 }} />
                      </Stack>

                      {plan.generationPrompt && (
                        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, px: 1.5, py: 0.75, mb: 1,
                          border: '1px solid', borderColor: 'grey.200' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            "{plan.generationPrompt}"
                          </Typography>
                        </Box>
                      )}

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.disabled">{formatDate(plan.updatedAt || plan.createdAt)}</Typography>
                        <Chip label="AI Generated" size="small"
                          icon={<AutoAwesome sx={{ fontSize: '12px !important' }} />}
                          sx={{ bgcolor: '#7C4DFF22', color: '#7C4DFF', fontWeight: 600, fontSize: 10 }} />
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {totalPlans} total plan{totalPlans !== 1 ? 's' : ''} · Read-only view
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Membership Freeze / Pause Modal with Prorated Date & Audit Log ──────────────
function FreezeMembershipModal({ user, onClose, onRefresh }) {
  const [history, setHistory] = useState([]);
  const [remainingDays, setRemainingDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const todayStr = new Date().toISOString().slice(0, 10);
  const nextWeekStr = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    startDate: todayStr,
    endDate: nextWeekStr,
    reason: 'Vacation / Travel',
  });

  const loadPauseInfo = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await pauseAPI.getPauseHistory(user._id);
      setHistory(res.data.data || []);
      setRemainingDays(res.data.remainingPauseDays ?? user.remainingPauseDays ?? 30);
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadPauseInfo(); }, [loadPauseInfo]);

  if (!user) return null;

  // Prorated expiry date calculation
  let freezeDays = 0;
  if (form.startDate && form.endDate) {
    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    if (end > start) {
      freezeDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    }
  }

  const currentExpiry = user.membershipEndDate || user.membershipExpiryDate
    ? new Date(user.membershipEndDate || user.membershipExpiryDate)
    : null;

  let proratedExpiry = null;
  if (currentExpiry && freezeDays > 0) {
    proratedExpiry = new Date(currentExpiry.getTime() + freezeDays * 24 * 60 * 60 * 1000);
  }

  const handleInitiate = async () => {
    setError('');
    setSuccess('');
    if (!form.startDate || !form.endDate) {
      setError('Please select both start and end freeze dates.');
      return;
    }
    if (freezeDays > remainingDays) {
      setError(`Freeze duration (${freezeDays} days) exceeds remaining quota (${remainingDays} days).`);
      return;
    }

    setSubmitting(true);
    try {
      await pauseAPI.initiatePause({
        userId: user._id,
        gymId: typeof user.gymId === 'object' ? user.gymId._id : user.gymId,
        startDate: form.startDate,
        endDate: form.endDate,
        reason: form.reason,
      });
      setSuccess('Membership freeze initiated successfully!');
      loadPauseInfo();
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate membership freeze');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResume = async (pauseId) => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      await pauseAPI.cancelPause(pauseId);
      setSuccess('Membership resumed successfully!');
      loadPauseInfo();
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resume membership');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        Rule-Based Membership Freeze & Audit Engine — {user.name}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Quota & Status Banner */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#F8FAFC', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                CURRENT STATUS
              </Typography>
              <Chip
                label={(user.membershipStatus || 'active').toUpperCase()}
                color={user.membershipStatus === 'paused' ? 'warning' : user.membershipStatus === 'active' ? 'success' : 'default'}
                size="small"
                sx={{ fontWeight: 800, mt: 0.5 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                REMAINING FREEZE QUOTA
              </Typography>
              <Typography variant="h6" fontWeight={800} color="primary.main">
                {remainingDays} Days / Year
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                CURRENT EXPIRY DATE
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {currentExpiry ? currentExpiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Initiate Freeze Form */}
        <Typography variant="subtitle2" fontWeight={800} mb={1.5}>
          Freeze Membership (Initiate New Pause)
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              type="date"
              label="Start Date"
              fullWidth
              size="small"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              type="date"
              label="End Date"
              fullWidth
              size="small"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Freeze Reason"
              fullWidth
              size="small"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            >
              <MenuItem value="Vacation / Travel">Vacation / Travel</MenuItem>
              <MenuItem value="Medical Injury / Illness">Medical Injury / Illness</MenuItem>
              <MenuItem value="Work / Exam Commitments">Work / Exam Commitments</MenuItem>
              <MenuItem value="Personal Reasons">Personal Reasons</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        {/* Live Prorated Expiry Calculation Box */}
        {freezeDays > 0 && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Prorated Expiry Extension Preview
            </Typography>
            <Typography variant="body2">
              Freeze duration: <strong>{freezeDays} days</strong>.
              {proratedExpiry && currentExpiry && (
                <>
                  {' '}Membership expiry date will automatically extend from{' '}
                  <strong>{currentExpiry.toLocaleDateString('en-IN')}</strong> ➔{' '}
                  <strong style={{ color: '#16a34a' }}>{proratedExpiry.toLocaleDateString('en-IN')}</strong>.
                </>
              )}
            </Typography>
          </Alert>
        )}

        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button
            variant="contained"
            color="warning"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PauseCircle />}
            onClick={handleInitiate}
            disabled={submitting || freezeDays <= 0}
          >
            Initiate Freeze ({freezeDays} Days)
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Audit Log Table */}
        <Typography variant="subtitle2" fontWeight={800} mb={1.5}>
          Membership Freeze Audit History & Logs
        </Typography>
        {loading ? (
          <CircularProgress size={24} />
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            No previous freeze records found for this member.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Days Consumed</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reason / Audit</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((record) => (
                  <TableRow key={record._id} hover>
                    <TableCell>{new Date(record.startDate).toLocaleDateString()}</TableCell>
                    <TableCell>{record.endDate ? new Date(record.endDate).toLocaleDateString() : 'Open-ended'}</TableCell>
                    <TableCell>{record.daysConsumed || 0} days</TableCell>
                    <TableCell>{record.reason || 'Admin Freeze'}</TableCell>
                    <TableCell>
                      <Chip
                        label={record.status}
                        size="small"
                        color={record.status === 'active' ? 'warning' : record.status === 'completed' ? 'success' : 'default'}
                        sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {record.status === 'active' && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled={submitting}
                          onClick={() => handleResume(record._id)}
                        >
                          Resume Now
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}